import React, { useEffect, useMemo, useState } from 'react';
import {
  ExternalLink, Globe, Shield, Phone, BookOpen, ShoppingBag,
  AlertCircle, Loader2, Wrench, Printer, ClipboardList,
  ShieldCheck, Database, Building2, Mail,
} from 'lucide-react';
import MyLayout from './Layout';
import { apiFetchJson } from './api';

const PUBLIC_SITES = [
  {
    icon: Shield,
    color: 'text-red-400',
    bg: 'bg-red-500/10',
    name: 'METERIS Cybersécurité',
    url: 'https://cyber.meteris.fr',
    desc: 'Site dédié aux solutions cybersécurité METERIS.',
    status: 'public_site',
  },
  {
    icon: Phone,
    color: 'text-purple-400',
    bg: 'bg-purple-500/10',
    name: 'Yeastar Téléphonie',
    url: 'https://yeastar.meteris.fr',
    desc: 'Espace partenaire téléphonie IP Yeastar.',
    status: 'public_site',
  },
  {
    icon: Wrench,
    color: 'text-orange-400',
    bg: 'bg-orange-500/10',
    name: 'Atelier Météris',
    url: 'https://atelier.meteris.fr',
    desc: 'Site particuliers dédié à la réparation, au dépannage et à la vente locale.',
    status: 'public_site',
  },
  {
    icon: Printer,
    color: 'text-cyan-400',
    bg: 'bg-cyan-500/10',
    name: 'METERIS MPS',
    url: 'https://mps.meteris.fr',
    desc: 'Site dédié aux copieurs, à l’impression managée et à la GED.',
    status: 'public_site',
  },
  {
    icon: BookOpen,
    color: 'text-green-400',
    bg: 'bg-green-500/10',
    name: 'MCF Formation',
    url: 'https://mcf-formation.fr',
    desc: 'Centre de formation certifié Qualiopi.',
    status: 'public_site',
  },
];

const INTERNAL_PORTALS = [
  {
    icon: ClipboardList,
    color: 'text-indigo-400',
    bg: 'bg-indigo-500/10',
    name: 'Portail Techniciens',
    url: 'https://portail.meteris.fr',
    desc: "Portail d'intervention et de comptes-rendus destiné aux techniciens.",
    status: 'internal_portal',
  },
  {
    icon: ShieldCheck,
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    name: 'MSP ESET',
    url: 'https://msp.eset.com',
    desc: 'Gestion des licences et du parc ESET MSP.',
    status: 'internal_portal',
  },
  {
    icon: Shield,
    color: 'text-lime-400',
    bg: 'bg-lime-500/10',
    name: 'ESET Protect',
    url: 'https://protect.eset.com/?origin=ema',
    desc: 'Console de protection ESET Protect Cloud.',
    status: 'internal_portal',
  },
  {
    icon: Database,
    color: 'text-sky-400',
    bg: 'bg-sky-500/10',
    name: 'Sauvegardes',
    url: 'https://acp.meteris.fr',
    desc: 'Console de supervision et de gestion des sauvegardes.',
    status: 'internal_portal',
  },
  {
    icon: Phone,
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
    name: 'Helpdesk',
    url: 'https://helpdesk.meteris.fr',
    desc: 'Suivi des tickets, interventions et support client.',
    status: 'internal_portal',
  },
  {
    icon: Globe,
    color: 'text-fuchsia-400',
    bg: 'bg-fuchsia-500/10',
    name: 'Omada Local',
    url: 'https://omada.meteris.fr:8443',
    desc: 'Accès direct au contrôleur Omada hébergé chez METERIS.',
    status: 'internal_portal',
  },
  {
    icon: Globe,
    color: 'text-pink-400',
    bg: 'bg-pink-500/10',
    name: 'Omada Cloud',
    url: 'https://omada.tplinkcloud.com/#controller',
    desc: 'Accès cloud au contrôleur TP-Link Omada.',
    status: 'internal_portal',
  },
  {
    icon: Phone,
    color: 'text-violet-400',
    bg: 'bg-violet-500/10',
    name: 'Téléphonie Yeastar',
    url: 'https://meteris.ras.yeastar.com/',
    desc: 'Administration des systèmes de téléphonie Yeastar.',
    status: 'internal_portal',
  },
  {
    icon: Building2,
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
    name: 'ERP Odoo',
    url: 'https://erp.meteris.fr/odoo',
    desc: "Acces a l'ERP Odoo METERIS.",
    status: 'internal_portal',
  },
  {
    icon: Mail,
    color: 'text-rose-400',
    bg: 'bg-rose-500/10',
    name: 'Antispam Hornetsecurity',
    url: 'https://cp.hornetsecurity.com/email_livetracking',
    desc: 'Suivi de livraison et supervision antispam Hornetsecurity.',
    status: 'internal_portal',
  },
];

const INTEGRATED_SITE_PRESETS = [
  {
    slug: 'boutique',
    name: 'Boutique Atelier Météris',
    management_url: '/my/boutique',
    type: 'ecommerce',
  },
];

function getSiteBadge(site) {
  if (site.status === 'integrated') {
    if (site.uiState === 'loading') {
      return { label: 'Connexion...', className: 'bg-white/5 text-gray-300' };
    }
    if (site.uiState === 'syncing') {
      return { label: 'Synchronisation', className: 'bg-[#0099D6]/10 text-[#0099D6]' };
    }
    if (site.health?.status === 'ok' || site.statusValue === 'online') {
      return { label: 'Intégré', className: 'bg-[#0099D6]/10 text-[#0099D6]' };
    }
    return { label: 'Dégradé', className: 'bg-amber-500/10 text-amber-400' };
  }

  if (site.status === 'internal_portal') {
    return { label: 'Interne', className: 'bg-indigo-500/10 text-indigo-300' };
  }

  return { label: 'Public', className: 'bg-white/5 text-gray-400' };
}

export default function Sites() {
  const [integratedSites, setIntegratedSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    setError('');
    setLoadingSites(true);
    apiFetchJson('/api/admin/sites', {}, 'Erreur de chargement des sites')
      .then(data => {
        if (active) setIntegratedSites(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        if (active) setError(err.message || 'Erreur de chargement des sites');
      })
      .finally(() => {
        if (active) setLoadingSites(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const sites = useMemo(() => {
    const sitesBySlug = new Map(
      integratedSites.map(site => [site.slug || site.name || site.management_url, site]),
    );

    const integrated = INTEGRATED_SITE_PRESETS.map(preset => {
      const site = sitesBySlug.get(preset.slug);
      const uiState = loadingSites ? 'loading' : site ? 'ready' : 'syncing';
      const desc = loadingSites
        ? "Connexion au site en cours. La carte reste accessible pendant la récupération des informations."
        : site?.last_error?.message
          ? `Gestion via portail. Dernier état: ${site.last_error.message}`
          : site
            ? 'Gestion centralisée via le portail principal.'
            : "Le site est disponible dans le portail. Les informations d'état sont en cours de récupération.";

      return {
        icon: ShoppingBag,
        color: 'text-[#0099D6]',
        bg: 'bg-[#0099D6]/10',
        name: site?.name || preset.name,
        url: site?.management_url || preset.management_url,
        desc,
        status: 'integrated',
        statusValue: site?.status || 'unknown',
        internal: true,
        uiState,
        meta: site || preset,
        key: preset.slug,
      };
    });

    const extraIntegrated = integratedSites
      .filter(site => !INTEGRATED_SITE_PRESETS.some(preset => preset.slug === site.slug))
      .map(site => ({
      icon: ShoppingBag,
      color: 'text-[#0099D6]',
      bg: 'bg-[#0099D6]/10',
      name: site.name || 'Site intégré',
      url: site.management_url || '/my/boutique',
      desc: site.last_error?.message
        ? `Gestion via portail. Dernier état: ${site.last_error.message}`
        : 'Gestion centralisée via le portail principal.',
      status: 'integrated',
      statusValue: site.status || 'unknown',
      internal: true,
      uiState: 'ready',
      meta: site,
      key: site.slug || site.name || site.management_url,
    }));

    return {
      integrated: [...integrated, ...extraIntegrated],
      publicSites: PUBLIC_SITES,
      internalPortals: INTERNAL_PORTALS,
    };
  }, [integratedSites, loadingSites]);

  const sections = [
    {
      key: 'integrated',
      title: 'Sites intégrés',
      description: 'Ces espaces sont pilotés directement depuis le portail METERIS.',
      items: sites.integrated,
    },
    {
      key: 'public',
      title: 'Sites publics',
      description: 'Vitrines et sites métiers accessibles publiquement.',
      items: sites.publicSites,
    },
    {
      key: 'internal',
      title: 'Portails internes',
      description: 'Consoles d’administration et outils d’exploitation avec authentification séparée.',
      items: sites.internalPortals,
    },
  ];

  return (
    <MyLayout title="Sites de l'écosystème">
      <div className="space-y-6">
        <p className="text-gray-400 text-sm">
          Accédez aux différents sites du groupe METERIS. Les sites marqués <span className="text-[#0099D6]">Intégré</span> sont administrables directement depuis ce back-office.
        </p>

        {error && (
          <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-sm text-red-400">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        {sections.map(section => (
          <section key={section.key} className="space-y-4">
            <div>
              <h2 className="text-white text-sm font-semibold">{section.title}</h2>
              <p className="text-gray-500 text-xs mt-1">{section.description}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {section.items.map(site => {
                const badge = getSiteBadge(site);
                return (
                  <div
                    key={site.key || site.name}
                    className="bg-[#12141a] border border-white/5 rounded-2xl p-5 hover:border-white/10 transition-colors"
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-xl ${site.bg} flex items-center justify-center shrink-0`}>
                        <site.icon className={`w-5 h-5 ${site.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-white font-semibold text-sm">{site.name}</h3>
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${badge.className}`}>
                            {badge.label}
                          </span>
                        </div>
                        <p className="text-gray-500 text-xs mb-3">{site.desc}</p>
                        {site.status === 'integrated' && site.uiState !== 'ready' && (
                          <div className="inline-flex items-center gap-2 text-[11px] text-gray-400 mb-3">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            {site.uiState === 'loading'
                              ? 'Récupération des informations de connexion...'
                              : "Le site est visible immédiatement, l'état détaillé arrive ensuite."}
                          </div>
                        )}
                        <a
                          href={site.url}
                          target={site.internal ? undefined : '_blank'}
                          rel={site.internal ? undefined : 'noopener noreferrer'}
                          className="inline-flex items-center gap-1.5 text-xs text-[#0099D6] hover:text-[#0284C7] transition-colors font-medium"
                        >
                          <Globe className="w-3.5 h-3.5" />
                          {site.status === 'integrated'
                            ? 'Administrer'
                            : site.status === 'internal_portal'
                              ? 'Ouvrir la console'
                              : 'Ouvrir le site'}
                          {!site.internal && <ExternalLink className="w-3 h-3" />}
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}

        <div className="bg-[#12141a] border border-dashed border-white/10 rounded-2xl p-6 text-center">
          <Globe className="w-8 h-8 text-gray-700 mx-auto mb-3" />
          <p className="text-gray-600 text-sm">D'autres sites et consoles pourront être ajoutés ici ensuite.</p>
        </div>
      </div>
    </MyLayout>
  );
}
